import React from 'react';
import { Typography, Stack, TextField, CircularProgress, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useGetEncounterQuery } from '../../../../services/EncounterService';
import SectionPaper from '../../../common/components/styled-ui-elements/SectionPaper';

interface PatientInfoViewProps {
    // Optional props for future customization
    variant?: 'default' | 'compact';
    showTitle?: boolean;
}

export const PatientInfoView: React.FC<PatientInfoViewProps> = ({ 
    variant = 'default',
    showTitle = true 
}) => {
    const [searchParams] = useSearchParams();
    const refundId = searchParams.get('id');
    
    // Extract encounter ID by removing the refund number (last segment) from refund ID
    // Format: company-center-mrn-visit-refundnumber -> company-center-mrn-visit
    const getEncounterId = (refundId: string | null): string => {
        if (!refundId) return '';
        const parts = refundId.split('-');
        if (parts.length >= 5) {
            // Remove the last part (refund number) to get encounter ID
            return parts.slice(0, -1).join('-');
        }
        return refundId; // fallback to original if format unexpected
    };

    const encounterId = getEncounterId(refundId);
    
    // Fetch encounter data using the derived encounter ID
    const { data: encounterData, isLoading, error } = useGetEncounterQuery(
        { id: encounterId },
        { 
            skip: !encounterId 
        }
    );

    if (!refundId) {
        return (
            <SectionPaper>
                <Alert severity="warning">
                    Missing refund ID parameter
                </Alert>
            </SectionPaper>
        );
    }

    if (isLoading) {
        return (
            <SectionPaper sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                    Loading patient information...
                </Typography>
            </SectionPaper>
        );
    }

    if (error) {
        return (
            <SectionPaper>
                <Alert severity="warning">
                    Unable to load patient information from encounter
                </Alert>
            </SectionPaper>
        );
    }

    // Helper function to format date
    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch {
            return '';
        }
    };

    return (
        <SectionPaper>
            {showTitle && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 2 }}>
                    Patient Information (Read-Only)
                </Typography>
            )}
            <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Account Number"
                        value={encounterData?.caseIdentification?.accountNumber || ''}
                        size="small"
                        disabled
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Service Date"
                        type="date"
                        value={encounterData?.encounterInfo?.serviceDate ? encounterData.encounterInfo.serviceDate.split('T')[0] : ''}
                        size="small"
                        disabled
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                    />
                </Stack>
                <Stack direction="row" spacing={2}>
                    <TextField
                        label="First Name"
                        value={encounterData?.patient?.firstName || ''}
                        size="small"
                        disabled
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Last Name"
                        value={encounterData?.patient?.lastName || ''}
                        size="small"
                        disabled
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Status"
                        value={encounterData?.status || ''}
                        size="small"
                        disabled
                        sx={{ flex: 1 }}
                    />
                </Stack>
            </Stack>
        </SectionPaper>
    );
};

export default PatientInfoView;
